/**
 * Game data types shared by the sim layers, the SDK, the sim-runner and the
 * server (brief §5.1 "Supporting types — define these in packages/shared").
 *
 * Everything here must stay JSON-serialisable and engine-neutral: these types
 * cross the browser/Node boundary in run records and verification.
 */

export const SCORED_GAME_IDS = ['chain', 'stack', 'bloodstream', 'tilt', 'dash'] as const;
export type ScoredGameId = (typeof SCORED_GAME_IDS)[number];

/**
 * The Garden is a GameId but never a ScoredGameId: it has no sim, no
 * determinism suite, and must never appear in the active-game selector
 * (brief §6.6).
 */
export type GameId = ScoredGameId | 'garden';

export type QualityTier = 'low' | 'medium' | 'high';

/**
 * One tick of player input. The array index in the run log IS the frame
 * number — a pause is a flag (PAUSE_MARKER), never a gap (brief §5.3).
 * Axes are quantised int16; floats are never fed to a sim.
 */
export interface InputFrame {
  /** Bit layout defined in input-bits.ts — shared code, do not improvise. */
  bits: number;
  /** int16, quantised. Never a float. */
  axisX: number;
  /** int16, quantised. Never a float. */
  axisY: number;
}

export interface RoundConfig {
  roundId: string;
  /**
   * Pins the run's product set (brief §5.5): verification must replay against
   * the same catalogue snapshot or choice points diverge and legitimate runs
   * get marked invalid.
   */
  catalogueVersion: string;
  /** Opaque tokens picked server-side at run start. The sim never reads the catalogue. */
  choiceProductIds: string[];
  difficultyProfile: 'standard';
  /**
   * Per-run parameters chosen before the run and PINNED server-side at issue
   * time so verification replays identically — e.g. Bloodstream's declared
   * Health Goal (§6.3), Dash's home locality (§6.5). Whitelisted per game.
   */
  gameParams?: Record<string, number | string> | undefined;
}

export interface ChoicePoint {
  choicePointId: string;
  /** The product ids on offer, in display order. */
  offeredIds: string[];
  /** Tick at which the choice opened (the sim pauses gameplay underneath). */
  openedAtTick: number;
  /** Ticks until the choice times out (a timeout is itself a signal). */
  timeoutTicks: number;
}

export interface RunTelemetry {
  durationTicks: number;
  pauseCount: number;
  choices: Array<{
    choicePointId: string;
    /** 0-based index into offeredIds; -1 = timed out. */
    chosenIndex: number;
    offeredIds: string[];
    /** Ticks from choice open to decision — decision speed is a preference signal (§5.5). */
    decisionTicks: number;
  }>;
  /** Per-game numeric extras, e.g. bloodstream orbs by goal. */
  gameSpecific: Record<string, number>;
}

/**
 * Garden botanicals (§6.6) — planting one is INGREDIENT-LEVEL affinity data,
 * finer than category. The Garden is a GameId but never a ScoredGameId: no
 * sim, no determinism suite, never in the active-game selector. L2 applies:
 * nothing in the Garden is ever redeemable, tradeable or worth money.
 */
export const BOTANICALS = [
  'ashwagandha',
  'turmeric',
  'echinacea',
  'elderberry',
  'chamomile',
  'valerian',
  'ginger',
] as const;
export type Botanical = (typeof BOTANICALS)[number];

export const GARDEN_SLOTS = 12;

/** Wall-clock growth stages (§6.6: growth continues while the player is away). */
export const GROWTH_STAGES = [
  { stage: 'seedling', afterHours: 0 },
  { stage: 'sprout', afterHours: 6 },
  { stage: 'blooming', afterHours: 24 },
  { stage: 'mature', afterHours: 72 },
] as const;
export type GrowthStage = (typeof GROWTH_STAGES)[number]['stage'];

export function growthStageFor(plantedAt: Date, now: Date = new Date()): GrowthStage {
  const hours = (now.getTime() - plantedAt.getTime()) / 3_600_000;
  let stage: GrowthStage = 'seedling';
  for (const g of GROWTH_STAGES) if (hours >= g.afterHours) stage = g.stage;
  return stage;
}

/** Maltese localities (§6.5): the same list the WooCommerce reports use. */
export const MALTA_LOCALITIES = [
  'Attard', 'Birkirkara', 'Birżebbuġa', 'Fgura', 'Gżira', 'Ħamrun', 'Luqa',
  'Marsaskala', 'Mellieħa', 'Mosta', 'Msida', 'Naxxar', 'Paola', 'Qormi',
  'Rabat', 'San Ġwann', 'Sliema', 'St Julian’s', 'Swieqi', 'Tarxien',
  'Valletta', 'Żabbar', 'Żebbuġ', 'Żejtun', 'Żurrieq', 'Gozo — Victoria', 'Other',
] as const;

/** The seven Health Goals (brief §6.3). Order is display order. */
export const HEALTH_GOALS = [
  'Sleep',
  'Immunity',
  'Energy',
  'Joint Care',
  'Gut Health',
  'Heart Health',
  'Collagen',
] as const;
export type HealthGoal = (typeof HEALTH_GOALS)[number];
